import { useCallback, useRef, type RefObject } from "react";

import type { IGrowthOverviewRowHoverProps } from "../table/GrowthOverviewGridRows";
import { GROWTH_OVERVIEW_ROW_HOVER_CLASS } from "../table/styled";

export const useGrowthOverviewRowHover = (gridRef: RefObject<HTMLElement | null>) => {
    const hoveredRowIdRef = useRef<string | null>(null);

    const clearHoveredRow = useCallback(() => {
        const grid = gridRef.current;
        if (!grid || hoveredRowIdRef.current == null) {
            return;
        }

        grid.querySelectorAll(`[data-row-id="${hoveredRowIdRef.current}"]`).forEach((element) => {
            element.classList.remove(GROWTH_OVERVIEW_ROW_HOVER_CLASS);
        });
        hoveredRowIdRef.current = null;
    }, [gridRef]);

    const handleRowMouseEnter = useCallback(
        (rowId: string) => {
            const grid = gridRef.current;
            if (!grid || hoveredRowIdRef.current === rowId) {
                return;
            }

            clearHoveredRow();
            hoveredRowIdRef.current = rowId;
            grid.querySelectorAll(`[data-row-id="${rowId}"]`).forEach((element) => {
                if (!element.hasAttribute("data-row-highlighted")) {
                    element.classList.add(GROWTH_OVERVIEW_ROW_HOVER_CLASS);
                }
            });
        },
        [clearHoveredRow, gridRef],
    );

    const handleRowMouseLeave = useCallback(() => {
        clearHoveredRow();
    }, [clearHoveredRow]);

    const getRowHoverProps = useCallback(
        (rowId: string): IGrowthOverviewRowHoverProps => ({
            rowId,
            onRowMouseEnter: () => handleRowMouseEnter(rowId),
            onRowMouseLeave: handleRowMouseLeave,
        }),
        [handleRowMouseEnter, handleRowMouseLeave],
    );

    return {
        handleRowMouseLeave,
        getRowHoverProps,
    };
};
